import { Button, PageContent, PageHeader } from '@/components';
import { useMinistries } from '@/features/master-table/hooks';
import { getMinistryName } from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import { useChangeBrowserTabTitleEffect, useId } from '@/hooks';
import { isPS } from '@/utils';
import { type FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { ProjectsPageDynamicLink } from '../../../_components/ProjectsPageDynamicLink';
import { IndonesiaProjectLedgerPage } from '../_components/IndonesiaProjectLedgerPage/IndonesiaProjectLedgerPage';

export const IndonesiaProjectLedgerElement: FC = () => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);

  const { id } = useId();
  const project = useProject(id);
  const ministries = useMinistries();

  const ministryName = getMinistryName({
    targetId: project.data?.ministryId,
    ministries: ministries.data,
  });

  const title = useMemo(
    () =>
      project.data &&
      ministryName &&
      `${project.data.fiscalYear}-${ministryName}-${project.data.name}`,
    [ministryName, project.data],
  );

  useChangeBrowserTabTitleEffect({
    title,
    hideFallback: true,
  });

  const handlePrint = useReactToPrint({
    contentRef,
    pageStyle: '@page { size: A4 landscape; margin: 10mm 5mm; }',
    documentTitle: title,
  });

  const handlePrintPdf = useCallback(() => {
    handlePrint();
  }, [handlePrint]);

  return (
    <>
      <PageHeader
        title="Indonesia Report Preview"
        breadcrumbs={[
          { title: <ProjectsPageDynamicLink /> },
          {
            title: t('project-details-page'),
            href: isPS()
              ? `${location.pathname.replace('/public-site', '').split('/').slice(0, -1).join('/')}`
              : `${location.pathname.split('/').slice(0, -1).join('/')}`,
          },
          {
            title: 'Indonesia Report Preview',
          },
        ]}
        buttons={
          <Button type="primary" onClick={handlePrintPdf}>
            {t('export-pdf')}
          </Button>
        }
      />
      <PageContent>
        <div ref={contentRef}>
          <IndonesiaProjectLedgerPage />
        </div>
      </PageContent>
    </>
  );
};
